import styled from 'styled-components'
import { TailSpin as Loader } from 'react-loader-spinner'
import { useCallback, useState } from 'react'
import { saveAs } from 'file-saver'
import { toast } from 'react-toastify'

import { LeftAlignedPageContent } from '../../../components/LayoutComponents'
import useQuery from '../../../hooks/useQuery'
import { postRequest } from '../../../lib/http'

import config from '../../../config'

const Heading = styled.span`
  font-size: 32px;
  font-weight: 600;
  margin: 8px 0px;
`
Heading.displayName = 'Heading'

const InfoPanelP = styled.p`
  margin: 8px 0px;
`
InfoPanelP.displayName = 'InfoPanelP'

const ReferenceCodePanel = styled.div`
  padding-top: 32px;
  padding-bottom: 32px;
  display: flex;
  flex-direction: column;
`
ReferenceCodePanel.displayName = 'ReferenceCodePanel'

const SubHeading = styled.span`
  font-size: 22px;
  font-weight: 600;
  margin: 8px 0px;
`
SubHeading.displayName = 'SubHeading'

const ReferenceCode = styled.span`
  font-size: 46px;
  margin: 22px 0px;
  font-weight: 800;
  color: ${({ theme }) => theme.palette.nswBrandDark};
`
ReferenceCode.displayName = 'ReferenceCode'

const ButtonContainer = styled.div`
  padding-top: 32px;
  padding-bottom: 32px;
`
ButtonContainer.displayName = 'ButtonContainer'

const PdfButtonContent = styled.div`
  display: flex;
`
PdfButtonContent.displayName = 'PdfButtonContent'

const LoaderStyled = styled(Loader)`
  margin-left: 8px;
`

export default function ActivityReportDraftReceipt() {
  const query = useQuery()
  const trackingCode = query.trackingCode?.toString() || undefined
  const email = query.email?.toString() || undefined

  const [isDownloadingPdf, setIsDownloadingPdf] = useState(false)
  const downloadPdf = useCallback(async () => {
    try {
      setIsDownloadingPdf(true)
      const { filename, data } = await postRequest<{
        filename: string
        data: string
      }>({
        url: `${config.HOSTED_API_URL}/activity-report/draft-pdf`,
        resource: {
          trackingCode,
        },
        addAuthHeaders: true,
      })

      const resp = await fetch(`data:application/pdf;base64,${data}`)
      saveAs(await resp.blob(), filename)
    } catch (e) {
      console.error(e)
      toast.error('There was an issue while trying to download your PDF', {
        position: 'bottom-right',
        hideProgressBar: true,
      })
    } finally {
      setIsDownloadingPdf(false)
    }
  }, [trackingCode])

  return (
    <LeftAlignedPageContent className="fa-activity-draft-receipt">
      <Heading>Form Saved</Heading>
      <SubHeading>Activity Report Form</SubHeading>
      <InfoPanelP>Your form has been saved and not yet submitted.</InfoPanelP>
      {trackingCode && (
        <ReferenceCodePanel>
          <b>Your reference code is:</b>
          <ReferenceCode className="fa-info-panel__tracking-code">
            {trackingCode}
          </ReferenceCode>
        </ReferenceCodePanel>
      )}

      <SubHeading>Next Steps</SubHeading>
      <InfoPanelP>
        Please download a copy of this form for approval by your General Manager
        or delegate.
      </InfoPanelP>
      <ButtonContainer className="fa-download-pdf">
        <button
          className="button is-primary"
          disabled={isDownloadingPdf}
          onClick={downloadPdf}
        >
          {isDownloadingPdf ? (
            <PdfButtonContent>
              <span>Downloading PDF</span>
              <LoaderStyled height={28} width={28} />
            </PdfButtonContent>
          ) : (
            <span>Download a PDF copy of your form</span>
          )}
        </button>
      </ButtonContainer>
      <SubHeading>Check your email</SubHeading>
      <InfoPanelP>
        We've sent instructions to your email address
        {email && <b> ({email})</b>} on how to return to the application when
        you are ready.
      </InfoPanelP>
      <InfoPanelP>
        If you didn't receive it, please contact{' '}
        <a href="mailto:food.contact@dpi.nsw.gov.au">
          food.contact@dpi.nsw.gov.au
        </a>{' '}
        as soon as possible as there may be a problem.
      </InfoPanelP>
    </LeftAlignedPageContent>
  )
}
